import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record'
import { formatMoney, formatNumber } from '@/lib/format'
import { SkeletonTable, isForbidden } from '@/admin/AdminShell'
import { PRICE_LISTS_KEY, RESOURCE, fetchProductPrices } from './pricingApi'

/**
 * Цены товара по прайсам на сегодня — для вкладки карточки товара.
 *
 * Показывает то, что вернул `GET /products/{id}/prices`: действующие сегодня прайсы и строки,
 * у каждой цены своя валюта. Цены разных валют стоят рядом и не складываются — итог «по
 * всем прайсам» означал бы выдуманный курс. Цена после скидки строки показывается рядом с
 * базовой, когда скидка есть: документ подставит именно её.
 *
 * Без права на прайсы — словами «нет доступа», а не пустой таблицей: пустота читалась бы
 * как «у товара нет цен».
 */
export function ProductPricesPanel({ productId }: { productId: string }) {
  const { t, locale, user } = useApp()
  const mayRead = can(user, RESOURCE, 'read')
  const prices = useQuery({
    queryKey: [...PRICE_LISTS_KEY, 'product', productId],
    enabled: mayRead,
    queryFn: ({ signal }) => fetchProductPrices(productId, undefined, signal),
  })

  if (!mayRead || isForbidden(prices.error)) {
    return <StateView kind="forbidden" compact title={t('pl.product.forbidden')} />
  }
  if (prices.isLoading) return <SkeletonTable columns={4} rows={3} label={t('pl.product.title')} />
  if (prices.error) {
    return (
      <StateView kind="error" compact title={t('pl.error')}
                 description={prices.error instanceof Error ? prices.error.message : undefined}
                 actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => void prices.refetch()}>{t('adm.retry')}</button>} />
    )
  }
  const rows = prices.data?.prices ?? []
  if (rows.length === 0) {
    return <StateView kind="empty" compact title={t('pl.product.none')} description={t('pl.product.none.text')} />
  }
  return (
    <div className="tbl-wrap">
      <table className="tbl" aria-label={t('pl.product.title')}>
        <thead>
          <tr>
            <th>{t('pl.product.list')}</th>
            <th>{t('pl.currency')}</th>
            <th className="num">{t('pl.product.price')}</th>
            <th className="num">{t('pl.product.from')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const discounted = Number(row.discountPercent) > 0
            return (
              <tr key={`${row.priceListId}-${row.minQuantity}`}>
                <td>
                  <Link to={`/price-lists/${row.priceListId}`}>{row.priceListName}</Link>
                  <span className="mono muted t-sm" style={{ marginLeft: 6 }}>{row.priceListCode}</span>
                  {row.defaultList && <span className="badge b-info" style={{ marginLeft: 6 }}>{t('pl.default')}</span>}
                </td>
                <td className="mono">{row.currency}</td>
                <td className="num mono">
                  {formatMoney(row.effectivePrice, row.currency, locale)}
                  {discounted && (
                    <span className="t-sm muted" style={{ marginLeft: 6 }}>
                      {t('pl.product.discount', {
                        base: formatMoney(row.price, row.currency, locale),
                        percent: formatNumber(Number(row.discountPercent), locale),
                      })}
                    </span>
                  )}
                </td>
                <td className="num">{Number(row.minQuantity) > 0 ? formatNumber(Number(row.minQuantity), locale) : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
